import { Component, computed, inject, input, output } from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { menuOutline } from 'ionicons/icons';

import { SessionService } from '../../core/session/session.service';
import { HelpMenuComponent } from './help-menu.component';
import { NotificationBellComponent } from './notification-bell.component';
import { UserMenuComponent } from './user-menu.component';

/**
 * The global top bar, ported from the Blazor TopMenu: brand (the active client's logo when it has
 * one) on the left, the user dropdown — which holds the client picker — on the right. It spans the
 * FULL width, with the sidebar and the page below it.
 *
 * Painted with the theme primary, so it follows the active client's palette exactly as the Blazor
 * navbar does (`navbar-theme`).
 *
 * The hamburger toggles the sidebar at ANY width — it is not an Ionic menu button, because the
 * sidebar is no longer an ion-menu: a user on a wide screen can collapse it to get the space back.
 */
@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    HelpMenuComponent,
    NotificationBellComponent,
    UserMenuComponent,
  ],
  styles: `
    /* The brand chip, ported from the Blazor theme's .navbar-brand img.navbar-logo. It exists because
       most client logos are transparent PNGs designed for a white page: on the themed (dark) bar they
       vanish. LogoBackgroundColor gives such a logo a legible backing, and ThemeService writes it to
       --theme-logo-bg. Default transparent, so a logo that already reads well is left alone. */
    img.brand-logo {
      background-color: var(--theme-logo-bg, transparent);
      padding: 3px 6px;
      border-radius: 4px;
    }
  `,
  template: `
    <ion-header>
      <ion-toolbar color="primary">
        <ion-buttons slot="start">
          <ion-button
            (click)="toggleSidebar.emit()"
            [attr.aria-label]="sidebarOpen() ? 'Hide menu' : 'Show menu'"
          >
            <ion-icon slot="icon-only" name="menu-outline"></ion-icon>
          </ion-button>
        </ion-buttons>

        <ion-title>
          <span class="flex items-center gap-2">
            @if (logoUrl()) {
              <img [src]="logoUrl()" [alt]="clientName()" class="brand-logo h-8 w-auto" />
            }
            <span>{{ clientName() }}</span>
          </span>
        </ion-title>

        <ion-buttons slot="end">
          <!-- Help → Send feedback. Renders nothing on a host with nowhere to send it. -->
          <app-help-menu />
          <!-- The bell the Blazor TopMenu had, back where it was. Left of the user menu, as before. -->
          <app-notification-bell />
          <app-user-menu />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
  `,
})
export class TopBarComponent {
  private readonly session = inject(SessionService);

  /** Purely for the button's aria-label — the layout owns the state. */
  readonly sidebarOpen = input(true);

  readonly toggleSidebar = output<void>();

  protected readonly clientName = computed(
    () => this.session.session()?.activeClientName || 'Core',
  );

  protected readonly logoUrl = computed(() => this.session.session()?.activeClientLogoUrl || '');

  constructor() {
    addIcons({ menuOutline });
  }
}
